
import { useState, useEffect } from 'react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthTab = 'login' | 'register';
type LoginMethod = 'phone' | 'wechat';

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [activeTab, setActiveTab] = useState<AuthTab>('login');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('phone');
  const [loginForm, setLoginForm] = useState({ phone: '', code: '' });
  const [registerForm, setRegisterForm] = useState({ phone: '', code: '', name: '' });
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMsg, setSubmitMsg] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSubmitStatus('idle');
      setSubmitMsg('');
      setCodeSent(false);
      setCountdown(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSendCode = () => {
    const phone = activeTab === 'login' ? loginForm.phone : registerForm.phone;
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setSubmitMsg('请输入正确的手机号');
      return;
    }
    setSubmitMsg('');
    setCodeSent(true);
    setCountdown(60);
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^1[3-9]\d{9}$/.test(loginForm.phone)) {
      setSubmitMsg('请输入正确的手机号');
      return;
    }
    if (!loginForm.code || loginForm.code.length < 4) {
      setSubmitMsg('请输入验证码');
      return;
    }
    setSubmitStatus('loading');
    try {
      const body = new URLSearchParams();
      body.append('phone', loginForm.phone);
      body.append('code', loginForm.code);
      body.append('type', 'login');
      const res = await fetch('https://readdy.ai/api/form/d6glpo1tp4hj31tchoeg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setSubmitMsg('登录成功！');
        setTimeout(() => onClose(), 1500);
      } else {
        setSubmitStatus('error');
        setSubmitMsg('提交失败，请稍后重试');
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMsg('网络异常，请稍后重试');
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^1[3-9]\d{9}$/.test(registerForm.phone)) {
      setSubmitMsg('请输入正确的手机号');
      return;
    }
    if (!registerForm.code || registerForm.code.length < 4) {
      setSubmitMsg('请输入验证码');
      return;
    }
    setSubmitStatus('loading');
    try {
      const body = new URLSearchParams();
      body.append('phone', registerForm.phone);
      body.append('code', registerForm.code);
      body.append('name', registerForm.name);
      body.append('type', 'register');
      const res = await fetch('https://readdy.ai/api/form/d6glpn9tp4hj31tchoe0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setSubmitMsg('注册成功！');
        setTimeout(() => onClose(), 1500);
      } else {
        setSubmitStatus('error');
        setSubmitMsg('提交失败，请稍后重试');
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMsg('网络异常，请稍后重试');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[420px] mx-4 overflow-hidden animate-fadeIn">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Header */}
        <div className="px-8 pt-8 pb-0">
          <div className="flex items-center gap-2 mb-1">
            <img
              src="https://public.readdy.ai/ai/img_res/10bc846b-f2c2-44b8-b0c7-278d7c8001fa.png"
              alt="星贸云"
              className="h-8 object-contain"
            />
          </div>
          <p className="text-xs text-gray-400 mt-1 mb-5">跨境出口合规管理平台</p>

          {/* Tabs */}
          <div className="flex gap-0 border-b border-gray-100">
            {(['login', 'register'] as AuthTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setSubmitStatus('idle'); setSubmitMsg(''); }}
                className={`pb-3 px-1 mr-6 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-[#14B8A6] text-[#14B8A6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'login' ? '登录' : '注册'}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {/* Login Tab */}
          {activeTab === 'login' && (
            <div>
              {/* Login Method Toggle */}
              <div className="flex gap-2 mb-5 p-1 bg-gray-50 rounded-full">
                <button
                  onClick={() => setLoginMethod('phone')}
                  className={`flex-1 py-2 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    loginMethod === 'phone'
                      ? 'bg-white text-[#14B8A6] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  手机号登录
                </button>
                <button
                  onClick={() => setLoginMethod('wechat')}
                  className={`flex-1 py-2 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    loginMethod === 'wechat'
                      ? 'bg-white text-[#14B8A6] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  微信扫码登录
                </button>
              </div>

              {loginMethod === 'phone' ? (
                <form data-readdy-form onSubmit={handleLoginSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">手机号</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="请输入手机号"
                      maxLength={11}
                      value={loginForm.phone}
                      onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                      className="w-full h-11 px-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#14B8A6] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">验证码</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="code"
                        placeholder="请输入验证码"
                        maxLength={6}
                        value={loginForm.code}
                        onChange={(e) => setLoginForm({ ...loginForm, code: e.target.value })}
                        className="flex-1 h-11 px-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={countdown > 0}
                        className={`h-11 px-4 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                          countdown > 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-[#14B8A6]/10 text-[#14B8A6] hover:bg-[#14B8A6]/20'
                        }`}
                      >
                        {countdown > 0 ? `${countdown}s 后重发` : codeSent ? '重新发送' : '获取验证码'}
                      </button>
                    </div>
                  </div>

                  {submitMsg && (
                    <p className={`text-xs ${submitStatus === 'success' ? 'text-[#14B8A6]' : 'text-red-500'}`}>
                      {submitMsg}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitStatus === 'loading'}
                    className="w-full h-11 bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitStatus === 'loading' ? '登录中...' : '登录'}
                  </button>
                </form>
              ) : (
                /* WeChat QR */
                <div className="flex flex-col items-center py-4">
                  <div className="w-44 h-44 bg-gray-50 border border-gray-100 rounded-xl flex flex-col items-center justify-center gap-3 mb-4">
                    <div className="w-32 h-32 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                      {/* QR Code placeholder */}
                      <div className="grid grid-cols-5 gap-0.5 p-2">
                        {Array.from({ length: 25 }).map((_, i) => (
                          <div
                            key={i}
                            className={`w-4 h-4 rounded-sm ${
                              [0,1,2,3,4,5,9,10,14,15,19,20,21,22,23,24,6,12,18,7,11,17].includes(i)
                                ? 'bg-gray-800'
                                : 'bg-white'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[#07C160]">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-wechat-fill text-lg"></i>
                    </div>
                    <span className="text-sm font-medium">微信扫码登录</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center leading-relaxed">
                    使用微信扫描二维码<br />自动读取手机号完成绑定
                  </p>
                  <div className="mt-4 px-4 py-2.5 bg-amber-50 border border-amber-100 rounded-lg w-full">
                    <p className="text-xs text-amber-600 text-center">
                      <i className="ri-information-line mr-1"></i>
                      首次扫码将自动绑定微信手机号并完成注册
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Register Tab */}
          {activeTab === 'register' && (
            <div>
              {/* Register Method Toggle */}
              <div className="flex gap-2 mb-5 p-1 bg-gray-50 rounded-full">
                <button
                  onClick={() => setLoginMethod('phone')}
                  className={`flex-1 py-2 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    loginMethod === 'phone'
                      ? 'bg-white text-[#14B8A6] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  手机号注册
                </button>
                <button
                  onClick={() => setLoginMethod('wechat')}
                  className={`flex-1 py-2 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    loginMethod === 'wechat'
                      ? 'bg-white text-[#14B8A6] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  微信扫码注册
                </button>
              </div>

              {loginMethod === 'phone' ? (
                <form data-readdy-form onSubmit={handleRegisterSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">手机号</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="请输入手机号"
                      maxLength={11}
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                      className="w-full h-11 px-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#14B8A6] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">验证码</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="code"
                        placeholder="请输入验证码"
                        maxLength={6}
                        value={registerForm.code}
                        onChange={(e) => setRegisterForm({ ...registerForm, code: e.target.value })}
                        className="flex-1 h-11 px-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={countdown > 0}
                        className={`h-11 px-4 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                          countdown > 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-[#14B8A6]/10 text-[#14B8A6] hover:bg-[#14B8A6]/20'
                        }`}
                      >
                        {countdown > 0 ? `${countdown}s 后重发` : codeSent ? '重新发送' : '获取验证码'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">姓名（选填）</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="请输入您的姓名"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      className="w-full h-11 px-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#14B8A6] transition-colors"
                    />
                  </div>

                  {submitMsg && (
                    <p className={`text-xs ${submitStatus === 'success' ? 'text-[#14B8A6]' : 'text-red-500'}`}>
                      {submitMsg}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitStatus === 'loading'}
                    className="w-full h-11 bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitStatus === 'loading' ? '注册中...' : '立即注册'}
                  </button>

                  <p className="text-xs text-gray-400 text-center">
                    注册即代表同意
                    <a href="#" className="text-[#14B8A6] hover:underline mx-0.5">服务协议</a>
                    与
                    <a href="#" className="text-[#14B8A6] hover:underline mx-0.5">隐私政策</a>
                  </p>
                </form>
              ) : (
                /* WeChat QR Register */
                <div className="flex flex-col items-center py-4">
                  <div className="w-44 h-44 bg-gray-50 border border-gray-100 rounded-xl flex flex-col items-center justify-center gap-3 mb-4">
                    <div className="w-32 h-32 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                      <div className="grid grid-cols-5 gap-0.5 p-2">
                        {Array.from({ length: 25 }).map((_, i) => (
                          <div
                            key={i}
                            className={`w-4 h-4 rounded-sm ${
                              [0,2,4,5,9,10,12,14,15,19,20,22,24,1,3,6,8,16,18,21,23].includes(i)
                                ? 'bg-gray-800'
                                : 'bg-white'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[#07C160]">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-wechat-fill text-lg"></i>
                    </div>
                    <span className="text-sm font-medium">微信扫码注册</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center leading-relaxed">
                    使用微信扫描二维码<br />自动读取手机号完成注册绑定
                  </p>
                  <div className="mt-4 px-4 py-2.5 bg-amber-50 border border-amber-100 rounded-lg w-full">
                    <p className="text-xs text-amber-600 text-center">
                      <i className="ri-information-line mr-1"></i>
                      扫码后将自动读取微信手机号并完成账号绑定
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
